import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useAppSelector } from '@/hooks/store';
import { useLocationState } from '@/hooks/useLocationState';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { FlexContainer } from '@/ui/styled-ui';
import { BOOK_MEETING_URLS } from '@/utils/helpers/book-meeting.helper';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PageContainer = styled(FlexContainer)`
  position: relative;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f8fa;
  border-radius: var(--default-border-radius);
  overflow: auto;
`;

const IframeContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--default-border-radius);
`;

const StyledIframe = styled.iframe`
  border: none;
  transform-origin: center;
  transition: transform 0.3s ease;
  pointer-events: auto;

  @media (max-height: 739px) {
    width: ${100 / 0.4}%;
    height: ${100 / 0.4}%;
    transform: scale(0.4);
  }

  @media (min-height: 740px) {
    width: ${100 / 0.5}%;
    height: ${100 / 0.5}%;
    transform: scale(0.5);
  }

  @media (min-height: 840px) {
    width: ${100 / 0.6}%;
    height: ${100 / 0.6}%;
    transform: scale(0.6);
  }

  @media (min-height: 940px) {
    width: ${100 / 0.75}%;
    height: ${100 / 0.75}%;
    transform: scale(0.75);
  }

  @media (min-height: 1440px) {
    width: 100%;
    height: 100%;
    transform: scale(1);
  }
`;

const LoadingContainer = styled(FlexContainer)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--beige-main);
`;

const StyledH1 = styled.h1`
  font-size: var(--heading-small-line-height);
  font-weight: 600;
  text-transform: uppercase;
  margin-top: 90px;
`;

const StyledP = styled.p`
  font-size: var(--heading-small-line-height);
  font-weight: 400;
  margin: 0;
  margin-bottom: calc(var(--medium-padding) * 2);
`;

const CloseButton = styled.div`
  position: absolute;
  top: var(--medium-padding);
  right: var(--medium-padding);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: var(--small-button-height);
  height: var(--small-button-height);
  cursor: pointer;

  &:hover {
    i {
      color: var(--red-main);
    }
  }
`;

export const HubspotBookingPage: React.FC = () => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { locationFrom } = useLocationState();

  const bookingUrl =
    account?.customerSuccessManager?.name &&
    Object.keys(BOOK_MEETING_URLS).includes(
      account?.customerSuccessManager?.name,
    )
      ? BOOK_MEETING_URLS[
          account?.customerSuccessManager
            ?.name as keyof typeof BOOK_MEETING_URLS
        ]
      : BOOK_MEETING_URLS['Anders Winther'];

  const getBookingUrlWithParams = () => {
    const url = new URL(bookingUrl);
    url.searchParams.append('embed', 'true');
    return url.toString();
  };

  return (
    <PageContainer>
      <CloseButton
        onClick={() =>
          navigate(locationFrom() || RedirectPaths[RedirectPathsEnum.BASE])
        }
      >
        <i className="pi pi-times" />
      </CloseButton>
      <StyledH1 className="mb-0">{t('hubspot.bookMeeting')}</StyledH1>
      <StyledP>{t('hubspot.bookMeetingDescription')}</StyledP>
      <IframeContainer>
        {isLoading && (
          <LoadingContainer justify="center" align="center">
            <ProgressSpinner />
          </LoadingContainer>
        )}
        <StyledIframe
          src={getBookingUrlWithParams()}
          onLoad={() => setIsLoading(false)}
          title={t('hubspot.bookMeeting')}
          scrolling="no"
        />
      </IframeContainer>
    </PageContainer>
  );
};
